import { VOICE_AGENT_VOICES } from "@jugl-web/rest-api/integrations";
import { LIVE_CHAT_DEFAULT_SETTINGS } from "../consts";
import type { LiveChatAppSettings } from "../types";

/**
 * Sanitizes a raw settings object coming from an untrusted source
 * (API response, external website config, URL params, etc.).
 * The input is `unknown` because runtime data may be missing, malformed,
 * or manually modified. Always returns a fully valid `LiveChatAppSettings`
 * object with safe defaults applied for any missing or invalid fields.
 */
export const resolveLiveChatAppSettings = (
  input: unknown
): LiveChatAppSettings => {
  const resolved: LiveChatAppSettings = structuredClone(
    LIVE_CHAT_DEFAULT_SETTINGS
  );

  const dict =
    input !== null && typeof input === "object" && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : null;

  if (!dict) {
    return resolved;
  }

  if (typeof dict.browserTabTitle === "string") {
    resolved.browserTabTitle = dict.browserTabTitle;
  }

  if (typeof dict.faviconUrl === "string") {
    resolved.faviconUrl = dict.faviconUrl;
  }

  if (typeof dict.businessName === "string") {
    resolved.businessName = dict.businessName;
  }

  if (typeof dict.businessLogoUrl === "string") {
    resolved.businessLogoUrl = dict.businessLogoUrl;
  }

  if (typeof dict.agentName === "string") {
    resolved.agentName = dict.agentName;
  }

  if (typeof dict.agentImage === "string") {
    resolved.agentImage = dict.agentImage;
  }

  if (dict.customButton === null) {
    resolved.customButton = null;
  } else if (
    dict.customButton &&
    typeof dict.customButton === "object" &&
    !Array.isArray(dict.customButton)
  ) {
    const raw = dict.customButton as Record<string, unknown>;

    if (typeof raw.title === "string" && typeof raw.url === "string") {
      resolved.customButton =
        raw.title.trim() && raw.url.trim()
          ? { title: raw.title, url: raw.url }
          : null;
    } else {
      resolved.customButton = null;
    }
  }

  if (dict.theme === "light" || dict.theme === "dark") {
    resolved.theme = dict.theme;
  }

  if (typeof dict.senderMessageBackgroundColor === "string") {
    resolved.senderMessageBackgroundColor = dict.senderMessageBackgroundColor;
  }

  if (Array.isArray(dict.suggestions)) {
    resolved.suggestions = dict.suggestions.filter(
      (s): s is string => typeof s === "string"
    );
  }

  if (typeof dict.ctaTitle === "string") {
    resolved.ctaTitle = dict.ctaTitle;
  }

  if (typeof dict.ctaSubtitle === "string") {
    resolved.ctaSubtitle = dict.ctaSubtitle;
  }

  if (dict.ctaVisual === null) {
    resolved.ctaVisual = null;
  } else if (
    dict.ctaVisual &&
    typeof dict.ctaVisual === "object" &&
    !Array.isArray(dict.ctaVisual)
  ) {
    const raw = dict.ctaVisual as Record<string, unknown>;

    if (typeof raw.id === "string" && typeof raw.url === "string") {
      resolved.ctaVisual = { id: raw.id, url: raw.url };
    } else {
      resolved.ctaVisual = null;
    }
  }

  if (
    dict.voice_config &&
    typeof dict.voice_config === "object" &&
    !Array.isArray(dict.voice_config)
  ) {
    const raw = dict.voice_config as Record<string, unknown>;

    if (typeof raw.enabled === "boolean") {
      resolved.voice_config.enabled = raw.enabled;
    }

    const matchingVoice = VOICE_AGENT_VOICES.find(
      (voice) => voice === raw.voice
    );

    if (matchingVoice) {
      resolved.voice_config.voice = matchingVoice;
    }
  }

  return resolved;
};
